import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ExternalLink, Calendar, Download, Paperclip, Share2, ArrowLeft, Newspaper, UserCheck, Send, Trash2, Pencil, Check, X as XIcon, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import usePageMeta from '@/hooks/usePageMeta';
import Linkify from '@/lib/linkify';
import LinkPreviews from '@/components/LinkPreviews';
import RichText from '@/components/RichText';
import RichTextEditor from '@/components/RichTextEditor';
import PageSideRail from '@/components/PageSideRail';
import { SponsorBanner } from '@/components/SponsorBanner';
import MemberAvatar from '@/components/MemberAvatar';

const BACKEND_URL = '';

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return 'Just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function NewsCommentsSection({ newsId, user }) {
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const load = useCallback(async () => {
    if (!newsId) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/news/${newsId}/comments`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || []);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [newsId]);

  useEffect(() => { load(); }, [load]);

  const handleAuthorClick = (userId) => {
    if (!userId) return;
    if (userId === user?.user_id) { navigate('/profile'); return; }
    navigate(`/profile/public/${userId}`);
  };

  const submit = async () => {
    const content = text.trim();
    if (!content) { toast.error('Please write a comment'); return; }
    setPosting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/news/${newsId}/comments`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });
      if (res.ok) {
        const c = await res.json();
        setComments(prev => [...prev, c]);
        setText('');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to post comment');
      }
    } catch { toast.error('Failed to post comment'); }
    setPosting(false);
  };

  const saveEdit = async (commentId) => {
    const content = editText.trim();
    if (!content) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/news/${newsId}/comments/${commentId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content }),
      });
      if (res.ok) {
        const updated = await res.json();
        setComments(prev => prev.map(c => c.comment_id === commentId ? { ...c, ...updated } : c));
        setEditingId(null); setEditText('');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update');
      }
    } catch { toast.error('Failed to update'); }
  };

  const remove = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/news/${newsId}/comments/${commentId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) setComments(prev => prev.filter(c => c.comment_id !== commentId));
      else toast.error('Failed to delete');
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <Card className="p-5 md:p-7 border-0 shadow-sm mt-6" data-testid="news-article-comments">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="h-4 w-4 text-brand-navy" />
        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide">
          Comments {comments.length > 0 && <span className="text-slate-400 font-medium">({comments.length})</span>}
        </h3>
      </div>

      {/* Composer */}
      <div className="flex items-start gap-3 mb-5" data-testid="news-comment-composer">
        <MemberAvatar name={user?.name} picture={user?.picture} size={36} />
        <div className="flex-1">
          <RichTextEditor
            value={text}
            onChange={setText}
            placeholder="Share a thought or drop a URL — it will unfurl as a link preview."
            testId="news-comment-input"
            minHeight="5rem"
          />
          <div className="flex justify-end mt-2">
            <Button
              onClick={submit}
              disabled={posting || !text.trim()}
              data-testid="news-comment-submit"
              className="bg-gradient-to-r from-brand-navy to-brand-sage text-white"
              size="sm"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" /> {posting ? 'Posting…' : 'Comment'}
            </Button>
          </div>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[0, 1].map(i => (
            <div key={i} className="animate-pulse flex gap-3">
              <div className="h-9 w-9 rounded-full bg-slate-100" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 w-1/4 bg-slate-100 rounded" />
                <div className="h-3 w-3/4 bg-slate-100 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : comments.length === 0 ? (
        <p className="text-sm text-slate-400 italic" data-testid="news-comments-empty">Be the first to comment on this article.</p>
      ) : (
        <div className="space-y-4">
          {comments.map(c => {
            const canEdit = c.author_id === user?.user_id || user?.role === 'admin';
            const isEditing = editingId === c.comment_id;
            return (
              <div key={c.comment_id} className="flex items-start gap-3" data-testid={`news-comment-${c.comment_id}`}>
                <button
                  type="button"
                  onClick={() => handleAuthorClick(c.author_id)}
                  data-testid={`news-comment-avatar-${c.comment_id}`}
                >
                  <MemberAvatar name={c.author_name} picture={c.author_picture} size={36} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => handleAuthorClick(c.author_id)}
                      className="text-sm font-semibold text-slate-800 hover:text-brand-navy transition-colors"
                      data-testid={`news-comment-author-${c.comment_id}`}
                    >
                      {c.author_name || 'Member'}
                    </button>
                    <span className="text-[11px] text-slate-400">
                      {timeAgo(c.created_at)}{c.edited_at ? ' · edited' : ''}
                    </span>
                    {canEdit && !isEditing && (
                      <div className="ml-auto flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => { setEditingId(c.comment_id); setEditText(c.content || ''); }}
                          className="text-slate-400 hover:text-brand-navy p-1"
                          data-testid={`news-comment-edit-${c.comment_id}`}
                          aria-label="Edit comment"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => remove(c.comment_id)}
                          className="text-slate-400 hover:text-rose-500 p-1"
                          data-testid={`news-comment-delete-${c.comment_id}`}
                          aria-label="Delete comment"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                  {isEditing ? (
                    <div className="mt-1.5">
                      <RichTextEditor
                        value={editText}
                        onChange={setEditText}
                        testId={`news-comment-edit-input-${c.comment_id}`}
                        minHeight="4rem"
                      />
                      <div className="flex gap-1.5 mt-1.5">
                        <Button size="sm" className="bg-brand-navy text-white h-7 text-xs px-2.5"
                          onClick={() => saveEdit(c.comment_id)}
                          data-testid={`news-comment-save-${c.comment_id}`}
                        >
                          <Check className="h-3 w-3 mr-1" /> Save
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs px-2.5"
                          onClick={() => { setEditingId(null); setEditText(''); }}
                        >
                          <XIcon className="h-3 w-3 mr-1" /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="text-sm text-slate-700 mt-1 break-words [overflow-wrap:anywhere]">
                        <RichText html={c.content} className="compact" />
                      </div>
                      <LinkPreviews text={c.content} />
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

/**
 * NewsArticlePage — full-page reader at /news/:newsId, replacing the modal.
 * Has its own URL (shareable / deep-linkable) and a banner-style hero so
 * the page treatment matches the rest of the inner pages.
 */
export default function NewsArticlePage({ user: userProp }) {
  const { newsId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [user, setUser] = useState(userProp || null);

  useEffect(() => {
    if (userProp) { setUser(userProp); return; }
    const cached = sessionStorage.getItem('sll_user') || localStorage.getItem('sll_user');
    if (cached) { try { setUser(JSON.parse(cached)); } catch { /* ignore */ } }
  }, [userProp]);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetch(`${BACKEND_URL}/api/news/single/${newsId}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d) setArticle(d);
        else setNotFound(true);
        setLoading(false);
      })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [newsId]);

  usePageMeta(article ? {
    title: article.title,
    description: article.summary?.slice(0, 200),
    image: article.image,
  } : { title: 'News' });

  const handleShare = async () => {
    const url = `${window.location.origin}/news/${article.news_id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: article.title, url });
        return;
      }
    } catch { /* fall through */ }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Article link copied!');
    } catch { toast.error('Could not copy link'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <main className="pt-24 pb-12 px-6">
          <div className="container mx-auto max-w-4xl animate-pulse">
            <div className="h-48 bg-slate-200 rounded-2xl mb-6" />
            <div className="h-8 bg-slate-200 rounded w-2/3 mb-3" />
            <div className="h-4 bg-slate-100 rounded w-1/3" />
          </div>
        </main>
      </div>
    );
  }

  if (notFound || !article) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <main className="pt-24 pb-12 px-6">
          <div className="container mx-auto max-w-2xl text-center">
            <p className="text-slate-500 mb-4">We couldn&apos;t find that article. It may have been removed.</p>
            <Button onClick={() => navigate('/news')} variant="outline"><ArrowLeft className="h-4 w-4 mr-2" /> Back to News</Button>
          </div>
        </main>
      </div>
    );
  }

  const dateStr = article.published_at
    ? new Date(article.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : '';
  const externalUrl = article.external_url || article.link;
  const isAdmin = article.source_type === 'admin';
  const isUserSubmission = article.source_type === 'user_submission';
  const banner = article.image;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <main className="pt-20 lg:pt-24 pb-12 px-3 md:px-6">
        <div className="container mx-auto max-w-6xl">
          <SponsorBanner placement={`news-article:${newsId}`} type="top" className="mb-6 flex justify-center" />
          <div className="flex flex-col xl:flex-row gap-6">
            <div className="flex-1 min-w-0">
              <button onClick={() => navigate('/news')}
                className="text-sm text-brand-navy hover:underline mb-3 inline-flex items-center gap-1"
                data-testid="news-article-back-btn">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to News
              </button>

              <Card className="overflow-hidden border-0 shadow-lg mb-6" data-testid="news-article-banner">
                <div
                  className={`relative ${banner ? '' : 'bg-gradient-to-br from-brand-navy via-brand-navy to-brand-sage'}`}
                  style={banner ? {
                    backgroundImage: `linear-gradient(180deg, rgba(15,23,42,0.2) 0%, rgba(15,23,42,0.78) 100%), url(${banner})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  } : undefined}
                >
                  <div className="p-6 md:p-10 text-white min-h-[180px] md:min-h-[240px] flex flex-col justify-end">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      {isAdmin && article.is_featured && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-brand-sage text-white uppercase tracking-wider">
                          <Newspaper className="h-3 w-3" /> Featured
                        </span>
                      )}
                      {article.is_sll_member_news && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-white/20 backdrop-blur-sm text-white uppercase tracking-wider">
                          SLL Member News
                        </span>
                      )}
                      {isUserSubmission && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-brand-blue text-white uppercase tracking-wider">
                          <UserCheck className="h-3 w-3" /> Submitted by member
                        </span>
                      )}
                    </div>
                    <h1 className="text-2xl md:text-4xl font-bold font-heading mb-3 drop-shadow-sm" data-testid="news-article-title">
                      {article.title}
                    </h1>
                    <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-white/90">
                      {article.author_name && article.author_name !== article.source && <span>By {article.author_name}</span>}
                      {article.submitted_by_name && !article.author_name && <span>via {article.submitted_by_name}</span>}
                      {dateStr && (
                        <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {dateStr}</span>
                      )}
                      {article.source && <span>· {article.source}</span>}
                    </div>
                  </div>
                </div>
              </Card>

              <div className="flex flex-wrap items-center gap-2 mb-6">
                <Button onClick={handleShare} variant="outline" data-testid="news-article-share-btn">
                  <Share2 className="h-4 w-4 mr-2" /> Share
                </Button>
                {externalUrl && (
                  <a href={externalUrl} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-md bg-brand-navy text-white hover:bg-brand-dark transition-colors"
                    data-testid="news-article-external-link">
                    Additional information <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>

              <Card className="p-5 md:p-7 border-0 shadow-sm space-y-6" data-testid="news-article-body">
                {article.topic_tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {article.topic_tags.map((tag, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                )}

                <RichText html={article.content || article.summary} className="text-slate-700 leading-relaxed" testId="news-article-content" />
                <LinkPreviews text={article.content || article.summary} />

                {article.attachments?.length > 0 && (
                  <div className="pt-4 border-t border-slate-100">
                    <p className="text-xs font-semibold text-slate-500 flex items-center gap-1 mb-2">
                      <Paperclip className="h-3 w-3" /> Attachments
                    </p>
                    <div className="space-y-1.5">
                      {article.attachments.map(att => (
                        <a key={att.attachment_id}
                          href={`${BACKEND_URL}/api/news/attachments/${att.attachment_id}/download`}
                          className="flex items-center gap-2 text-sm text-brand-navy hover:underline bg-slate-50 rounded px-3 py-2 border"
                          data-testid={`news-article-att-${att.attachment_id}`}>
                          <Download className="h-3.5 w-3.5" />
                          <span>{att.filename}</span>
                          <span className="text-slate-400 ml-auto text-xs">{(att.size / 1024).toFixed(0)} KB</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </Card>

              <NewsCommentsSection newsId={newsId} user={user} />
            </div>

            <PageSideRail placement={`news-article:${newsId}`} />
          </div>
        </div>
      </main>
    </div>
  );
}
